import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Target } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_LABELS = {
  personnel: 'Personnel', administrative: 'Administrative',
  project: 'Project', women_project: 'Women Project',
};
const PROGRAM_LABELS = { school: 'School Program', women: 'Women Empowerment', general: 'General / Org' };

const EMPTY = { category_group: '', subcategory: '', program: '', amount: '', notes: '' };

export default function BudgetsPage() {
  const { hasPermission } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canEdit = hasPermission('finance', 'edit');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/finance/budget-vs-actual`, { params: { year } });
      setData(r.data);
    } catch {
      toast.error('Failed to load budgets');
    }
  }, [year]);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/finance/taxonomy`).then((r) => setTaxonomy(r.data)).catch(() => {});
  }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ category_group: b.category_group, subcategory: b.subcategory || '', program: b.program || '', amount: String(b.amount), notes: b.notes || '' });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        year, category_group: form.category_group,
        subcategory: form.subcategory || null,
        program: form.program || null,
        amount: parseFloat(form.amount),
        notes: form.notes || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/finance/budgets/${editing.id}`, payload);
        toast.success('Budget line updated');
      } else {
        await axios.post(`${API_URL}/api/finance/budgets`, payload);
        toast.success('Budget line added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save budget');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/finance/budgets/${id}`);
      toast.success('Budget line deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const subOptions = taxonomy?.groups?.[form.category_group]?.subcategories || [];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="space-y-6" data-testid="budgets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Budget Management</h2>
          <p className="text-muted-foreground">Annual budgets per expense category and program · Budget vs Actual</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="budget-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={openCreate} data-testid="add-budget-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Budget Line
            </Button>
          )}
        </div>
      </div>

      {/* Group totals — every expense group covered */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="budget-group-totals">
          {data.group_totals.map((g) => (
            <Card key={g.category_group}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Target className="h-4 w-4 text-primary" /> {g.label}
                </div>
                <p className="text-xs text-muted-foreground">
                  Budget: <strong>{g.budget ? formatCurrency(g.budget) : '—'}</strong>
                </p>
                <p className="text-xs text-muted-foreground">
                  Actual: <strong className="text-red-600">{formatCurrency(g.actual)}</strong>
                </p>
                {g.budget > 0 && (
                  <>
                    <Progress value={Math.min(g.pct_used || 0, 100)} className="h-2" />
                    <p className={`text-xs font-medium ${(g.pct_used || 0) > 100 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {g.pct_used}% used · {formatCurrency(g.variance)} remaining
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base">Budget Lines — {year}</CardTitle>
          <CardDescription>Budget vs actual spend per category / subcategory / program</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Group</TableHead>
                <TableHead>Subcategory</TableHead>
                <TableHead>Program</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="w-40">Used</TableHead>
                {canEdit && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data || data.lines.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No budget lines for {year} yet{canEdit ? ' — click "Add Budget Line" to set one' : ''}
                </TableCell></TableRow>
              ) : data.lines.map((b) => (
                <TableRow key={b.id} className="table-row-hover" data-testid={`budget-row-${b.id}`}>
                  <TableCell><Badge variant="outline">{GROUP_LABELS[b.category_group] || b.category_group}</Badge></TableCell>
                  <TableCell className="capitalize">{b.subcategory ? b.subcategory.replace(/_/g, ' ') : <span className="text-muted-foreground text-xs">Whole group</span>}</TableCell>
                  <TableCell>{b.program ? PROGRAM_LABELS[b.program] : <span className="text-muted-foreground text-xs">All</span>}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(b.amount)}</TableCell>
                  <TableCell className="text-right text-red-600">{formatCurrency(b.actual)}</TableCell>
                  <TableCell className={`text-right font-medium ${b.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(b.variance)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={Math.min(b.pct_used, 100)} className="h-2 flex-1" />
                      <span className={`text-xs ${b.pct_used > 100 ? 'text-red-600 font-bold' : 'text-muted-foreground'}`}>{b.pct_used}%</span>
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => openEdit(b)} data-testid={`edit-budget-${b.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteTarget(b)} data-testid={`delete-budget-${b.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Budget Line' : `Add Budget Line — ${year}`}</DialogTitle>
            <DialogDescription>Leave subcategory empty to budget the whole group</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Expense Group *</Label>
              <Select value={form.category_group} onValueChange={(v) => setForm({ ...form, category_group: v, subcategory: '' })}>
                <SelectTrigger data-testid="budget-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(GROUP_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l} Expenses</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Subcategory</Label>
                <Select value={form.subcategory} onValueChange={(v) => setForm({ ...form, subcategory: v === '__all__' ? '' : v })}>
                  <SelectTrigger data-testid="budget-subcategory-select"><SelectValue placeholder="Whole group" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Whole group</SelectItem>
                    {subOptions.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Program</Label>
                <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v === '__all__' ? '' : v })}>
                  <SelectTrigger data-testid="budget-program-select"><SelectValue placeholder="All programs" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All programs</SelectItem>
                    <SelectItem value="school">School Program</SelectItem>
                    <SelectItem value="women">Women Empowerment</SelectItem>
                    <SelectItem value="general">General / Org</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Annual Budget (UGX) *</Label>
              <Input type="number" required value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="budget-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="budget-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!form.category_group || !form.amount} data-testid="budget-submit-btn">
                {editing ? 'Update Budget' : 'Save Budget'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-budget-dialog">
          <DialogHeader>
            <DialogTitle>Delete budget line?</DialogTitle>
            <DialogDescription>
              {deleteTarget && `${GROUP_LABELS[deleteTarget.category_group]} · ${formatCurrency(deleteTarget.amount)} — this cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove(deleteTarget.id)} data-testid="delete-budget-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
